/**
 * Test page for ArtifactSelectionList component
 *
 * This page demonstrates the new row-based artifact selection UI
 * with mock data to verify the design works correctly.
 */

import { useState } from 'react';
import { ArtifactSelectionList, type ArtifactGroup } from '../components/ui/ArtifactSelectionList';
import type { Artifact } from '../types/artifactDesign';

export const TestArtifactSelection: React.FC = () => {
  const [groups, setGroups] = useState<ArtifactGroup[]>([
    {
      type: 'skill',
      artifacts: [
        { id: 'skill-1', name: 'User Authentication', identifier: 'user_auth_111F', description: 'Authenticate users via OAuth', selected: true, status: 'ready' },
        { id: 'skill-2', name: 'Data Validation', identifier: 'data_validate_111F', description: 'Validate input data', selected: false, status: 'ready' },
        { id: 'skill-3', name: 'Email Notification', identifier: 'email_notify_111F', description: 'Send email notifications', selected: true, status: 'ready' },
        { id: 'skill-4', name: 'Report Generation', identifier: 'report_gen_111F', description: 'Generate PDF reports', selected: false, status: 'ready' },
        { id: 'skill-5', name: 'Database Sync', identifier: 'db_sync_111F', description: 'Synchronize database records', selected: true, status: 'ready' },
        { id: 'skill-6', name: 'File Upload', identifier: 'file_upload_111F', description: 'Handle file uploads', selected: false, status: 'ready' },
        { id: 'skill-7', name: 'API Gateway', identifier: 'api_gateway_111F', description: 'Route API requests', selected: false, status: 'ready' },
        { id: 'skill-8', name: 'Cache Management', identifier: 'cache_mgmt_111F', description: 'Manage application cache', selected: true, status: 'ready' },
      ]
    },
    {
      type: 'subskill',
      artifacts: [
        { id: 'subskill-1', name: 'Validate Email Format', identifier: 'validate_email_111F', description: 'Check email format validity', selected: true, status: 'ready' },
        { id: 'subskill-2', name: 'Check Password Strength', identifier: 'check_password_111F', description: 'Validate password complexity', selected: false, status: 'ready' },
      ]
    },
    {
      type: 'subflow',
      artifacts: [
        { id: 'subflow-1', name: 'User Registration Flow', identifier: 'user_reg_flow_111F', description: 'Complete user registration workflow', selected: true, status: 'ready' },
      ]
    },
    {
      type: 'attribute',
      artifacts: [
        { id: 'attr-1', type: 'attribute', name: 'User Email', identifier: 'user_email', description: 'User email address', dataType: 'TEXT', scope: 'GLOBAL', selected: true, usedBySkills: [], status: 'validated' },
        { id: 'attr-2', type: 'attribute', name: 'User ID', identifier: 'user_id', description: 'Unique user identifier', dataType: 'NUMBER', scope: 'GLOBAL', selected: false, usedBySkills: [], status: 'validated' },
        { id: 'attr-3', type: 'attribute', name: 'Session Token', identifier: 'session_token', description: 'Token for active session tracking', dataType: 'TEXT', scope: 'SESSION', selected: true, usedBySkills: [], status: 'validated' },
        { id: 'attr-4', type: 'attribute', name: 'Is Admin', identifier: 'is_admin', description: 'Flag indicating admin privileges', dataType: 'BOOLEAN', scope: 'GLOBAL', selected: false, usedBySkills: [], status: 'validated' },
        { id: 'attr-5', type: 'attribute', name: 'Last Login', identifier: 'last_login', description: 'Timestamp of last login', dataType: 'DATE', scope: 'GLOBAL', selected: true, usedBySkills: [], status: 'validated' },
        { id: 'attr-6', type: 'attribute', name: 'User Preferences', identifier: 'user_prefs', description: 'Serialized user preferences', dataType: 'JSON', scope: 'GLOBAL', selected: false, usedBySkills: [], status: 'validated' },
        { id: 'attr-7', type: 'attribute', name: 'Account Status', identifier: 'account_status', description: 'Lifecycle status of account', dataType: 'TEXT', scope: 'GLOBAL', selected: false, usedBySkills: [], status: 'validated' },
        { id: 'attr-8', type: 'attribute', name: 'Failed Login Count', identifier: 'failed_logins', description: 'Recent failed login tally', dataType: 'NUMBER', scope: 'SESSION', selected: false, usedBySkills: [], status: 'validated' },
      ]
    },
    {
      type: 'integration',
      artifacts: [
        { id: 'int-1', type: 'integration', name: 'Salesforce CRM', identifier: 'salesforce_111F', description: 'Salesforce REST API', integrationType: 'api', selected: true, usedBySkills: [], status: 'validated', operations: [], instances: [] },
        { id: 'int-2', type: 'integration', name: 'SendGrid Email', identifier: 'sendgrid_111F', description: 'Send transactional email via SendGrid', integrationType: 'api', selected: false, usedBySkills: [], status: 'validated', operations: [], instances: [] },
        { id: 'int-3', type: 'integration', name: 'Stripe Payments', identifier: 'stripe_111F', description: 'Manage payments with Stripe', integrationType: 'api', selected: true, usedBySkills: [], status: 'validated', operations: [], instances: [] },
        { id: 'int-4', type: 'integration', name: 'Twilio SMS', identifier: 'twilio_111F', description: 'Send SMS via Twilio', integrationType: 'api', selected: false, usedBySkills: [], status: 'validated', operations: [], instances: [] },
        { id: 'int-5', type: 'integration', name: 'AWS S3', identifier: 'aws_s3_111F', description: 'Store files in S3', integrationType: 'api', selected: false, usedBySkills: [], status: 'validated', operations: [], instances: [] },
        { id: 'int-6', type: 'integration', name: 'Google Analytics', identifier: 'ga_111F', description: 'Analytics reporting API', integrationType: 'api', selected: true, usedBySkills: [], status: 'validated', operations: [], instances: [] },
        { id: 'int-7', type: 'integration', name: 'Slack Notifications', identifier: 'slack_111F', description: 'Notify via Slack webhooks', integrationType: 'webhook', selected: false, usedBySkills: [], status: 'validated', operations: [], instances: [] },
      ]
    },
    {
      type: 'facet',
      artifacts: [
        { id: 'facet-1', type: 'facet', name: 'Error Handling', identifier: 'error_handling_111F', description: 'Handles errors gracefully', facetType: 'Exception Handler', selected: true, status: 'validated' },
        { id: 'facet-2', type: 'facet', name: 'Logging', identifier: 'logging_111F', description: 'Captures audit logs', facetType: 'Audit Trail', selected: false, status: 'validated' },
      ]
    }
  ]);

  const handleSelectionChange = (artifactId: string, artifactType: 'skill' | 'subskill' | 'subflow' | 'attribute' | 'integration' | 'facet', selected: boolean) => {
    setGroups(prevGroups =>
      prevGroups.map(group => {
        if (group.type === artifactType) {
          return {
            ...group,
            artifacts: group.artifacts.map(artifact =>
              artifact.id === artifactId ? { ...artifact, selected } : artifact
            )
          };
        }
        return group;
      })
    );
  };

  const handleTypeToggle = (artifactType: 'skill' | 'subskill' | 'subflow' | 'attribute' | 'integration' | 'facet', selected: boolean) => {
    setGroups(prevGroups =>
      prevGroups.map(group => {
        if (group.type === artifactType) {
          return {
            ...group,
            artifacts: group.artifacts.map(artifact => ({ ...artifact, selected }))
          };
        }
        return group;
      })
    );
  };

  const handleSelectAll = (selected: boolean) => {
    setGroups(prevGroups =>
      prevGroups.map(group => ({
        ...group,
        artifacts: group.artifacts.map(artifact => ({ ...artifact, selected }))
      }))
    );
  };

  const totalSelected = groups.reduce((sum, g) => sum + g.artifacts.filter(a => a.selected).length, 0);

  return (
    <div className="w-full h-screen flex flex-col bg-background">
      {/* Header */}
      <div className="h-12 border-b border-border bg-background flex items-center justify-between px-4">
        <h1 className="text-lg font-semibold">Artifact Selection Test Page</h1>
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground">{totalSelected} selected</span>
          <button className="px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded hover:bg-primary/90">
            Generate ({totalSelected})
          </button>
        </div>
      </div>

      {/* Artifact Selection List */}
      <div className="flex-1 overflow-auto">
        <ArtifactSelectionList
          groups={groups}
          onSelectionChange={handleSelectionChange}
          onTypeToggle={handleTypeToggle}
          onSelectAll={handleSelectAll}
          mode="generation"
        />
      </div>
    </div>
  );
};
